import { type UseMutationResult, useQueryClient } from '@tanstack/react-query'
import React, { createContext, useContext, useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { useCreateMemberOnboardingAdditionalParticipant } from 'components/hooks/member_onboarding_v2/useCreateMemberOnboardingAdditionalParticipant'
import { useDeleteEnrollment } from 'components/hooks/member_onboarding_v2/useDeleteEnrollment'
import { useDeleteMemberOnboardingAdditionalParticipant } from 'components/hooks/member_onboarding_v2/useDeleteMemberOnboardingAdditionalParticipant'
import { useGetMemberOnboardingAdditionalParticipants } from 'components/hooks/member_onboarding_v2/useGetMemberOnboardingAdditionalParticipants'
import { useGetMemberOnboardingInfo } from 'components/hooks/member_onboarding_v2/useGetMemberOnboardingInfo'
import { useUpdateMemberOnboardingAdditionalParticipant } from 'components/hooks/member_onboarding_v2/useUpdateMemberOnboardingAdditionalParticipant'
import { useUpdateMemberOnboardingInfo } from 'components/hooks/member_onboarding_v2/useUpdateMemberOnboardingInfo'
import {
  getGetMemberOnboardingEnrollmentEventsEnrollmentEventIdPlanChangeSummaryQueryKey as memberPlanChangeSummaryQueryKey,
  useGetMemberOnboardingEnrollmentEventsEnrollmentEventIdPlanChangeSummary as useGetMemberPlanChangeSummary,
} from 'utils/web_api'
import { type MemberOnboardingMemberPlanChangesProps } from 'utils/web_api.schemas'

import {
  type AdditionalParticipant,
  type AdditionalParticipants,
  type OnboardingInfo,
  type OnboardingInfoUpdateParams,
  type ParticipantFormProps,
} from './types'
import { isRenewal } from './utils'

export type OnboardingContextProps = {
  // Onboarding info related props
  onboardingData?: OnboardingInfo | null;
  isLoading: boolean;
  isError: boolean;
  error?: GrapeAPIErrorType | null;
  updateMemberInfo: UseMutationResult<
    OnboardingInfo,
    GrapeAPIErrorType,
    OnboardingInfoUpdateParams
  >;
  refreshOnboardingInfo: (updatedData?: OnboardingInfo | null) => void;
  // Additional participants related props
  additionalParticipants?: AdditionalParticipants | null;
  isAdditionalParticipantsLoading: boolean;
  isAdditionalParticipantsError: boolean;
  additionalParticipantsError?: GrapeAPIErrorType | null;
  createAdditionalParticipant: UseMutationResult<
    AdditionalParticipant,
    GrapeAPIErrorType,
    ParticipantFormProps
  >;
  updateAdditionalParticipant: (
    participantId: string
  ) => UseMutationResult<
    AdditionalParticipant,
    GrapeAPIErrorType,
    ParticipantFormProps
  >;
  deleteAdditionalParticipant: (
    participantId: string
  ) => UseMutationResult<void, GrapeAPIErrorType, void>;
  refreshAdditionalParticipants: () => void;
  refreshAdditionalParticipant: (participantId: string) => void;
  deleteEnrollment: (
    enrollmentId: string
  ) => UseMutationResult<void, GrapeAPIErrorType, void>;
  // Plan change summary (Renewals/QLE) related props
  planChangeSummary?: MemberOnboardingMemberPlanChangesProps;
  isPlanChangeSummaryLoading: boolean;
  isPlanChangeSummaryError: boolean;
  planChangeSummaryError: GrapeAPIErrorType | null;
  refreshPlanChangeSummary: () => void;
  // Other props
  employerName: string;
  enrollmentEventId: string;
  mapboxAccessToken: string;
  // Hawaii state restriction
  isHawaiiState: boolean;
  setIsHawaiiState: (value: boolean) => void;
  // Demo data prefill
  prefillDummyData: boolean;
  // Feature flags
  isMemberOnTieredAllowance: boolean | null;
  showOnboardingUnderMaintenanceBanner?: boolean | null;
  isFerryVerificationTasksEnabled?: boolean | null;
  showSmartBudget?: boolean | null;
  useAleraMedicareForm?: boolean | null;
  nvIncorrectPlanPricesBanner?: boolean | null;
}

export const OnboardingContext = createContext<OnboardingContextProps | undefined>(undefined)

export function OnboardingProvider ({
  children,
  employerName,
  enrollmentEventId,
  mapboxAccessToken,
  prefillDummyData = false,
  isMemberOnTieredAllowance,
  showOnboardingUnderMaintenanceBanner,
  isFerryVerificationTasksEnabled,
  showSmartBudget,
  useAleraMedicareForm,
  nvIncorrectPlanPricesBanner,
}: {
  children: React.ReactNode;
  employerName: string;
  enrollmentEventId: string;
  mapboxAccessToken: string;
  prefillDummyData?: boolean;
  isMemberOnTieredAllowance: boolean | null;
  showOnboardingUnderMaintenanceBanner?: boolean | null;
  isFerryVerificationTasksEnabled?: boolean | null;
  showSmartBudget?: boolean | null;
  useAleraMedicareForm?: boolean | null;
  nvIncorrectPlanPricesBanner?: boolean | null;
}) {
  const queryClient = useQueryClient()
  // Hook to get the onboarding info
  const {
    data: onboardingData,
    isLoading,
    isError,
    error,
  } = useGetMemberOnboardingInfo({
    enrollmentEventId,
  })

  const {
    data: additionalParticipants,
    isLoading: isAdditionalParticipantsLoading,
    isError: isAdditionalParticipantsError,
    error: additionalParticipantsError,
  } = useGetMemberOnboardingAdditionalParticipants({
    enrollmentEventId,
  })

  // Hook to update the onboarding info
  const updateMemberInfo = useUpdateMemberOnboardingInfo({
    enrollmentEventId,
  })

  const {
    data: planChangeSummary,
    isLoading: isPlanChangeSummaryLoading,
    isError: isPlanChangeSummaryError,
    error: planChangeSummaryError,
  } = useGetMemberPlanChangeSummary<
    MemberOnboardingMemberPlanChangesProps,
    GrapeAPIErrorType
  >(enrollmentEventId, {
    query: {
      enabled: onboardingData ? isRenewal(onboardingData) : false,
      refetchOnWindowFocus: false,
      retry: false,
    },
  })

  const refreshPlanChangeSummary = async () => {
    await queryClient.invalidateQueries({
      queryKey: memberPlanChangeSummaryQueryKey(enrollmentEventId),
    })
  }

  const refreshOnboardingInfo = async (updatedData?: OnboardingInfo | null) => {
    // Manually update cache with new data
    if (updatedData) {
      queryClient.setQueryData(
        ['enrollmentEvent', enrollmentEventId, 'onboarding'],
        updatedData
      )
      return updatedData
    } else {
      // Invalidate and wait for the refetch to complete
      await queryClient.invalidateQueries({
        queryKey: ['enrollmentEvent', enrollmentEventId, 'onboarding'],
        refetchType: 'active',
      })
      // Wait for the data to be updated in the cache
      return queryClient.getQueryData([
        'enrollmentEvent',
        enrollmentEventId,
        'onboarding',
      ])
    }
  }

  const deleteEnrollment = (enrollmentId: string) =>
    useDeleteEnrollment({ enrollmentEventId, enrollmentId })

  const createAdditionalParticipant =
    useCreateMemberOnboardingAdditionalParticipant({ enrollmentEventId })
  const updateAdditionalParticipant = (participantId: string) => {
    return useUpdateMemberOnboardingAdditionalParticipant({
      enrollmentEventId,
      participantId,
    })
  }
  const deleteAdditionalParticipant = (participantId: string) => {
    return useDeleteMemberOnboardingAdditionalParticipant({
      enrollmentEventId,
      participantId,
    })
  }
  const refreshAdditionalParticipants = () => {
    queryClient.invalidateQueries({
      queryKey: [
        'enrollmentEvent',
        enrollmentEventId,
        'additional_participants',
      ],
    })
  }

  const refreshAdditionalParticipant = (participantId: string) => {
    queryClient.invalidateQueries({
      queryKey: [
        'enrollmentEvent',
        enrollmentEventId,
        'additional_participants',
        participantId,
      ],
    })
  }

  // Add state for Hawaii restriction
  const [isHawaiiState, setIsHawaiiState] = useState(false)

  return (
    <OnboardingContext.Provider
      value={{
        onboardingData,
        isLoading,
        isError,
        error,
        updateMemberInfo,
        refreshOnboardingInfo,
        additionalParticipants,
        isAdditionalParticipantsLoading,
        isAdditionalParticipantsError,
        additionalParticipantsError,
        createAdditionalParticipant,
        updateAdditionalParticipant,
        deleteAdditionalParticipant,
        refreshAdditionalParticipants,
        refreshAdditionalParticipant,
        deleteEnrollment,
        employerName,
        enrollmentEventId,
        mapboxAccessToken,
        isHawaiiState,
        setIsHawaiiState,
        prefillDummyData, // for environments where we want to make it faster to get through onboarding by prefilling dummy data
        isMemberOnTieredAllowance,
        isFerryVerificationTasksEnabled,
        planChangeSummary,
        isPlanChangeSummaryLoading,
        isPlanChangeSummaryError,
        planChangeSummaryError,
        refreshPlanChangeSummary,
        showOnboardingUnderMaintenanceBanner,
        showSmartBudget,
        useAleraMedicareForm,
        nvIncorrectPlanPricesBanner,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  )
}

export function useOnboarding () {
  const context = useContext(OnboardingContext)
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider')
  }
  return context
}
